import React, { useState, useEffect } from 'react'
import { Download, Share2, Heart, Eye, EyeOff, Save, MessageCircle, Play, Info } from 'lucide-react'
import { FilterOptions } from '../types'
import { useAuth } from '../context/AuthContext'
import ImageMetadataModal from './ImageMetadataModal'

interface GeneratedImageProps {
  imageUrl: string
  prompt: string
  filters: FilterOptions
  model?: string
  footPersona?: {
    name: string
    prompt: string
  }
  generatedAt?: string
}

const GeneratedImage: React.FC<GeneratedImageProps> = ({ 
  imageUrl, 
  prompt, 
  filters, 
  model, 
  footPersona, 
  generatedAt 
}) => {
  const { user } = useAuth()
  const [isFavorited, setIsFavorited] = useState(false)
  const [isPublic, setIsPublic] = useState(false)
  const [imageTitle, setImageTitle] = useState('')
  const [showSaveOptions, setShowSaveOptions] = useState(false)
  const [showMetadata, setShowMetadata] = useState(false)
  
  // Foot Persona State
  const [footName, setFootName] = useState('')
  const [footPrompt, setFootPrompt] = useState('')
  const [savedFeet, setSavedFeet] = useState<any[]>([])
  const [showFootSection, setShowFootSection] = useState(false)

  useEffect(() => {
    const saved = localStorage.getItem('toefettiSavedFeet')
    if (saved) setSavedFeet(JSON.parse(saved))
  }, [])

  const handleDownload = async () => {
    try {
      console.log('📥 Starting image download:', { imageUrl })
      
      const filename = `toefetti_generated_${Date.now()}.png`
      
      // Try direct download first
      const link = document.createElement('a')
      link.href = imageUrl
      link.download = filename
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      console.log('✅ Download initiated for:', filename)
    } catch (error) {
      console.error('❌ Download failed:', error)
      
      // Fallback: open in new tab
      try {
        window.open(imageUrl, '_blank', 'noopener,noreferrer')
        alert('Download failed. Image opened in new tab - right-click and select "Save image as..." to download.')
      } catch (fallbackError) {
        alert('Download failed. Please right-click the image and select "Save image as..." to download manually.')
      }
    }
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Toefetti Generated Image',
          text: 'Check out this AI-generated feet image!',
          url: window.location.href
        })
      } catch (error) {
        console.log('Error sharing:', error)
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href)
      alert('Link copied to clipboard!')
    }
  }

  const getActiveFilters = () => {
    return Object.entries(filters)
      .filter(([_, value]) => Array.isArray(value) ? value.length > 0 : value !== '')
      .map(([key, value]) => ({ 
        key, 
        value: Array.isArray(value) ? value.join(', ') : value 
      }))
  }

  const activeFilters = getActiveFilters()

  const saveFoot = () => {
    if (!footName || !footPrompt) return
    
    const newFoot = { 
      id: `foot-${Date.now()}`,
      name: footName, 
      prompt: footPrompt, 
      filters, 
      imageUrl,
      createdAt: new Date().toISOString(),
      timesUsed: 0
    }
    const updated = [newFoot, ...savedFeet]
    setSavedFeet(updated)
    localStorage.setItem('toefettiSavedFeet', JSON.stringify(updated))
    setFootName('')
    setFootPrompt('')
    setShowFootSection(false)
    alert(`${footName} has been saved to your foot personas!`)
  }

  const speak = (text: string) => {
    if ('speechSynthesis' in window) {
      const utter = new window.SpeechSynthesisUtterance(text)
      utter.rate = 1.05
      utter.pitch = 1.1
      utter.lang = 'en-US'
      window.speechSynthesis.speak(utter)
    } else {
      alert('Speech synthesis not supported in this browser.')
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden animate-fade-in">
      {/* Image */}
      <div className="relative group">
        <img
          src={imageUrl}
          alt="Generated feet image"
          className="w-full h-64 object-cover"
        />
        
        {/* Hover overlay with info button */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
          <button
            onClick={() => setShowMetadata(true)}
            className="opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300 p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white shadow-lg"
            title="View image details"
          >
            <Info className="w-5 h-5 text-gray-700" />
          </button>
        </div>
        
        {/* Action buttons (top right) */}
        <div className="absolute top-4 right-4 flex space-x-2">
          {user && (
            <>
              <button 
                onClick={() => setIsFavorited(!isFavorited)}
                className={`p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors ${
                  isFavorited ? 'text-red-500' : 'text-gray-600'
                }`}
              >
                <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
              </button>
              <button 
                onClick={() => setShowSaveOptions(!showSaveOptions)}
                className="p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
              >
                <Save className="w-4 h-4 text-gray-600" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Prompt */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Generated Image</h3>
          <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
            "{prompt}"
          </p>
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Applied Filters</h4>
            <div className="flex flex-wrap gap-2">
              {activeFilters.map(({ key, value }) => (
                <span
                  key={key}
                  className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full"
                >
                  {key}: {value}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Save Options (for logged-in users) */}
        {user && showSaveOptions && (
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <h4 className="font-semibold text-blue-700 mb-3">Save Image Settings</h4>
            <div className="space-y-3">
              <input
                type="text"
                value={imageTitle}
                onChange={(e) => setImageTitle(e.target.value)}
                placeholder="Give your image a title..."
                className="w-full p-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsPublic(!isPublic)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                    isPublic 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  {isPublic ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  <span>{isPublic ? 'Public' : 'Private'}</span>
                </button>
                <span className="text-sm text-blue-600">
                  {isPublic ? 'Others can see this image' : 'Only you can see this image'}
                </span>
              </div>
              <div className="text-xs text-blue-600">
                <p><strong>Note:</strong> Image is already saved to your account automatically.</p>
                <p>Use these options to update title and privacy settings.</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={handleDownload}
            className="flex-1 btn-secondary flex items-center justify-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Download</span>
          </button>
          <button
            onClick={handleShare}
            className="flex-1 btn-primary flex items-center justify-center space-x-2"
          >
            <Share2 className="w-4 h-4" />
            <span>Share</span>
          </button>
        </div>

        {/* Save & Talk Section */}
        <div className="mt-6 p-4 bg-pink-50 rounded-xl border border-pink-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-pink-700">Create Foot Persona</h4>
            <button
              onClick={() => setShowFootSection(!showFootSection)}
              className="text-pink-600 hover:text-pink-700 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
            </button>
          </div>
          
          {showFootSection && (
            <div className="space-y-3">
              <input
                type="text"
                className="w-full p-2 rounded border border-pink-200 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Name your foot (e.g. Mistress Toesy)"
                value={footName}
                onChange={e => setFootName(e.target.value)}
              />
              <textarea
                className="w-full p-2 rounded border border-pink-200 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Describe how your dirty little foot talks to you..."
                value={footPrompt}
                onChange={e => setFootPrompt(e.target.value)}
                rows={3}
              />
              <div className="flex space-x-2">
                <button
                  className="btn-primary flex-1 flex items-center justify-center space-x-2"
                  onClick={() => {
                    if (footPrompt) speak(footPrompt)
                  }}
                  disabled={!footPrompt}
                >
                  <Play className="w-4 h-4" />
                  <span>Test Voice</span>
                </button>
                <button
                  className="btn-secondary flex items-center space-x-2"
                  onClick={saveFoot}
                  disabled={!footName || !footPrompt}
                >
                  <Save className="w-4 h-4" />
                  <span>Save Persona</span>
                </button>
              </div>
            </div>
          )}

          {!showFootSection && (
            <p className="text-sm text-pink-600">
              Create a talking persona for this foot that you can use later!
            </p>
          )}
        </div>

        {/* Recent Saved Feet Preview */}
        {savedFeet.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-pink-700">Your Recent Personas</h4>
              <span className="text-xs text-pink-600">{savedFeet.length} saved</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {savedFeet.slice(0, 4).map((foot, i) => (
                <div key={i} className="flex items-center space-x-2 p-2 bg-pink-100 rounded-lg">
                  <img src={foot.imageUrl} alt={foot.name} className="w-8 h-8 rounded object-cover border" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-pink-800 text-sm truncate">{foot.name}</div>
                    <div className="text-xs text-pink-600">Used {foot.timesUsed || 0} times</div>
                  </div>
                  <button
                    className="p-1 bg-pink-200 hover:bg-pink-300 rounded transition-colors"
                    onClick={() => speak(foot.prompt)}
                  >
                    <Play className="w-3 h-3 text-pink-700" />
                  </button>
                </div>
              ))}
            </div>
            {savedFeet.length > 4 && (
              <p className="text-xs text-pink-600 text-center mt-2">
                +{savedFeet.length - 4} more personas available
              </p>
            )}
          </div>
        )}
      </div>

      {/* Metadata Modal */}
      <ImageMetadataModal
        isOpen={showMetadata}
        onClose={() => setShowMetadata(false)}
        metadata={{
          prompt,
          filters,
          model,
          footPersona,
          generatedAt,
          imageUrl
        }}
      />
    </div>
  )
}

export default GeneratedImage 